/**
 * Browser HTTP layer for the media-v3 `/api/v3` upload surface.
 *
 * Pure `fetch` — nothing here imports server code, so it is safe to bundle into
 * the webapp. Every init/final/status call carries a bearer token; the raw part
 * PUTs to S3 carry no auth header. Callers pass a fresh token per request (parts
 * take time, so a token grabbed once can expire mid-upload).
 */

import { anySignal } from "@/libs/any-signal";
import type {
  FinalResult,
  InitItem,
  MediaUploadAccess,
  StatusItem,
  SttGrant,
  TranscriptDraftResult,
} from "../_types/media-upload";

/** Multipart convention: 10 MB per part (S3 requires every non-final part >= 5 MB). */
export const PART_SIZE_BYTES = 10 * 1024 * 1024;
/** Deadline for one part PUT — a 10 MB part on a slow uplink still fits. */
export const PART_TIMEOUT_MS = 180_000;
/** Parts in flight at once per file (S3 accepts any order; the browser caps sockets). */
export const PART_CONCURRENCY = 4;

/** The caller aborted the upload — a decision, not a failure; never retried. */
export class UploadCancelledError extends Error {
  constructor() {
    super("Upload cancelled");
    this.name = "UploadCancelledError";
  }
}

/** Total attempts per part before the whole file fails (1 try + 3 retries). */
export const PART_MAX_ATTEMPTS = 4;

/** Every failure surfaces the HTTP status plus the raw response body. */
export class ApiError extends Error {
  readonly status: number;
  readonly bodyText: string;

  constructor(message: string, status: number, bodyText: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.bodyText = bodyText;
  }
}

export interface MediaClientConfig {
  /** Origin of the media service, e.g. `https://media-staging.fly.dev`. */
  baseUrl: string;
  /** Clerk JWT (fetched fresh per call). Sent as a bearer whenever non-empty. */
  token: string;
}

/**
 * Which upload routes a call targets. `authed` is the default `/init` + `/final`
 * pair behind a Clerk bearer; `public` is the signed-out `/init-public` +
 * `/final-public` pair (vendor onboarding) — rate-limited, allowlisted content
 * types, size-capped, and never sent a bearer (the token is dropped even when
 * one exists, so a stale session can't leak into the anonymous path).
 */
export type { MediaUploadAccess };

/** Part count for a file: 10 MB per part, at least one. */
export function computePartCount(sizeBytes: number): number {
  return Math.max(1, Math.ceil(sizeBytes / PART_SIZE_BYTES));
}

function joinUrl(baseUrl: string, path: string): string {
  return `${baseUrl.replace(/\/+$/, "")}${path}`;
}

/** Pull a readable message out of the framework error envelope, else raw text. */
function messageFromBody(bodyText: string, status: number): string {
  try {
    const parsed: unknown = JSON.parse(bodyText);
    const envelope = parsed as { error?: { message?: string; code?: string } };
    if (envelope?.error?.message) {
      return `HTTP ${status} ${envelope.error.code ?? ""} — ${envelope.error.message}`.trim();
    }
  } catch {
    // Not JSON — fall through to the raw body.
  }
  return `HTTP ${status} — ${bodyText.slice(0, 500) || "(empty body)"}`;
}

async function requestJson<T>(
  config: MediaClientConfig,
  path: string,
  init: RequestInit,
): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  const token = config.token.trim();
  if (token) headers.Authorization = `Bearer ${token}`;

  let res: Response;
  try {
    res = await fetch(joinUrl(config.baseUrl, path), {
      ...init,
      headers,
      signal: AbortSignal.timeout(30_000),
    });
  } catch (err) {
    throw new ApiError(`Network error — ${(err as Error).message}`, 0, "");
  }

  const bodyText = await res.text();
  if (!res.ok) {
    throw new ApiError(
      messageFromBody(bodyText, res.status),
      res.status,
      bodyText,
    );
  }
  try {
    return (JSON.parse(bodyText) as { data: T }).data;
  } catch {
    throw new ApiError("Response was not JSON", res.status, bodyText);
  }
}

function accessConfig(
  config: MediaClientConfig,
  access: MediaUploadAccess,
): MediaClientConfig {
  return access === "public" ? { ...config, token: "" } : config;
}

/** `POST /uploads/init` (or `/init-public`) — request body is a BARE array. */
export function initUploads(
  config: MediaClientConfig,
  items: { fileName: string; parts: number; contentType?: string }[],
  access: MediaUploadAccess = "authed",
): Promise<InitItem[]> {
  const path =
    access === "public"
      ? "/api/v3/uploads/init-public"
      : "/api/v3/uploads/init";
  return requestJson<InitItem[]>(accessConfig(config, access), path, {
    method: "POST",
    body: JSON.stringify(items),
  });
}

const MISSING_ETAG_MESSAGE =
  "Part PUT succeeded but no ETag response header was readable — add `ETag` to the bucket CORS ExposeHeaders";

/** Bytes of one part the browser has pushed so far (fires as the socket drains). */
export type PartProgress = (bytesLoaded: number) => void;

/**
 * The browser transport for one part: `XMLHttpRequest`, the only API that
 * reports UPLOAD progress (fetch reports none), so a 10 MB part can drive a
 * smooth ring instead of a jump per part. Same contract as the fetch path.
 */
function putPartXhr(
  signedUrl: string,
  body: Blob,
  signal: AbortSignal | undefined,
  onProgress: PartProgress | undefined,
): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", signedUrl);
    xhr.timeout = PART_TIMEOUT_MS;
    xhr.upload.onprogress = (event) => onProgress?.(event.loaded);
    xhr.onload = () => {
      if (xhr.status < 200 || xhr.status >= 300) {
        reject(
          new ApiError(
            `Part PUT failed — HTTP ${xhr.status}`,
            xhr.status,
            xhr.responseText ?? "",
          ),
        );
        return;
      }
      const etag = xhr.getResponseHeader("ETag");
      if (!etag) {
        reject(new ApiError(MISSING_ETAG_MESSAGE, xhr.status, ""));
        return;
      }
      resolve(etag);
    };
    xhr.onerror = () =>
      reject(
        signal?.aborted
          ? new UploadCancelledError()
          : new ApiError("Part PUT network error", 0, ""),
      );
    xhr.ontimeout = () =>
      reject(
        new ApiError(`Part PUT timed out after ${PART_TIMEOUT_MS} ms`, 0, ""),
      );
    xhr.onabort = () => reject(new UploadCancelledError());
    if (signal?.aborted) {
      reject(new UploadCancelledError());
      return;
    }
    signal?.addEventListener("abort", () => xhr.abort(), { once: true });
    xhr.send(body);
  });
}

/**
 * PUT one part's raw bytes to its presigned URL and return the ETag.
 *
 * The ETag is read from the RESPONSE HEADER, which requires `ETag` in the bucket
 * CORS `ExposeHeaders` — a missing header is reported as exactly that, since it
 * is the single most common misconfiguration on this path.
 *
 * In a browser the part goes over XHR so `onProgress` can report bytes as they
 * leave; where XHR does not exist (tests) it goes over fetch, whole-part only.
 */
export async function putPart(
  signedUrl: string,
  body: Blob,
  signal?: AbortSignal,
  onProgress?: PartProgress,
): Promise<string> {
  if (typeof XMLHttpRequest !== "undefined") {
    return putPartXhr(signedUrl, body, signal, onProgress);
  }
  let res: Response;
  try {
    res = await fetch(signedUrl, {
      method: "PUT",
      body,
      signal: signal
        ? anySignal([AbortSignal.timeout(PART_TIMEOUT_MS), signal])
        : AbortSignal.timeout(PART_TIMEOUT_MS),
    });
  } catch (err) {
    if (signal?.aborted) throw new UploadCancelledError();
    throw new ApiError(
      `Part PUT network error — ${(err as Error).message}`,
      0,
      "",
    );
  }
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new ApiError(
      `Part PUT failed — HTTP ${res.status}`,
      res.status,
      text,
    );
  }
  const etag = res.headers.get("ETag") ?? res.headers.get("etag");
  if (!etag) {
    throw new ApiError(MISSING_ETAG_MESSAGE, res.status, "");
  }
  onProgress?.(body.size);
  return etag;
}

export interface PutPartOutcome {
  etag: string;
  /** How many attempts beyond the first were needed (0 on a clean upload). */
  retries: number;
}

/**
 * PUT one part with bounded exponential backoff. Parts are idempotent — the S3
 * key is fixed by PartNumber, so a re-PUT overwrites — which makes retrying
 * always safe. Backs off ~500ms, 1s, 2s (plus jitter) between attempts; only
 * after every attempt is exhausted does it rethrow the last error. `onRetry`
 * fires once per failed attempt that will be retried.
 */
export async function putPartWithRetry(
  signedUrl: string,
  body: Blob,
  onRetry?: (attempt: number, error: Error) => void,
  signal?: AbortSignal,
  onProgress?: PartProgress,
): Promise<PutPartOutcome> {
  let lastError: Error = new ApiError("Part upload failed", 0, "");
  for (let attempt = 1; attempt <= PART_MAX_ATTEMPTS; attempt++) {
    try {
      const etag = await putPart(signedUrl, body, signal, onProgress);
      return { etag, retries: attempt - 1 };
    } catch (err) {
      lastError = err as Error;
      // A cancellation is the caller's decision, never something to retry through.
      if (err instanceof UploadCancelledError || signal?.aborted) throw err;
      if (attempt >= PART_MAX_ATTEMPTS) break;
      // The re-PUT starts the part's bytes over.
      onProgress?.(0);
      onRetry?.(attempt, lastError);
      const base = 500 * 2 ** (attempt - 1);
      const jitter = Math.floor(Math.random() * 250);
      await new Promise((resolve) => setTimeout(resolve, base + jitter));
    }
  }
  throw lastError;
}

/** `POST /uploads/final` (or `/final-public`) — PartNumber is a STRING here. */
export function finalUpload(
  config: MediaClientConfig,
  body: {
    fileKey: string;
    fileId: string;
    attachmentId: string;
    parts: { PartNumber: string; ETag: string }[];
    /** `false` skips the AI analysis — both `/final` and `/final-public` honor it. */
    runAiAnalysis?: boolean;
  },
  access: MediaUploadAccess = "authed",
): Promise<FinalResult> {
  const path =
    access === "public"
      ? "/api/v3/uploads/final-public"
      : "/api/v3/uploads/final";
  return requestJson<FinalResult>(accessConfig(config, access), path, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/** `GET /uploads/status?attachmentIds=a,b` — always requires a bearer token. */
export function fetchStatus(
  config: MediaClientConfig,
  attachmentIds: string[],
): Promise<StatusItem[]> {
  const query = encodeURIComponent(attachmentIds.join(","));
  return requestJson<StatusItem[]>(
    config,
    `/api/v3/uploads/status?attachmentIds=${query}`,
    { method: "GET" },
  );
}

/** `POST /uploads/stt-grant` — mint a short-lived token for one live-transcript stream. */
export function fetchSttGrant(config: MediaClientConfig): Promise<SttGrant> {
  return requestJson<SttGrant>(config, "/api/v3/uploads/stt-grant", {
    method: "POST",
    body: "{}",
  });
}

/** `POST /uploads/transcript` — store the live transcript as the attachment's draft. */
export function postTranscriptDraft(
  config: MediaClientConfig,
  body: { attachmentId: string; text: string; model: string },
): Promise<TranscriptDraftResult> {
  return requestJson<TranscriptDraftResult>(
    config,
    "/api/v3/uploads/transcript",
    { method: "POST", body: JSON.stringify(body) },
  );
}
